import { useEffect, useMemo, useRef, useState } from 'react';

import { useStatsigClient } from '@statsig/react-bindings';

import type {
  CarryForwardLogic,
  Group,
  Question,
  QuestionAnswer,
  Template,
} from '@api/instant_survey/types';
import {
  createQuestion,
  ERROR_SAVE_DRAFT,
  FOLDER_ID_CUSTOM,
  QUESTION_LOGIC_OPERATORS,
  QUESTION_TYPES,
  QUESTION_TYPES_WITH_OPTIONS,
  QUESTION_TYPES_WITH_SUBQUESTIONS,
  SURVEY_TYPES,
  updateQuestion,
} from '@api/instant_survey';
import { maxQuestionError, minQuestions } from '@api/surveys';

import { GATES } from '@constants';
import { useAppSelector } from '@redux/hooks';
import {
  ActionArea,
  Announcement,
  Button,
  IconButton,
  isBetweenMinAndMax,
  OverlayTriggerTooltip,
  toast,
  Toolbar,
} from '@utilities';
import { ContractIcon, ExpandIcon, GPTOutlinedIcon, OverviewIcon } from '@utilities/icons';
import DownloadDOCXButton from '../../../../components/DownloadDOCXButton';

import {
  getIsPreviewDisabled,
  PreviewButton,
  PreviewModal,
  useCanPreviewSurveys,
  useSurveyPreview,
} from '../../../../components/SurveyPreview';
import {
  calculateQuestionNumbers,
  findQuestionLogicQuestionById,
  getQuestionsWithScreenersFirst,
  getSequentialMonadicQuestionnaireValidationError,
  isValidQuestionLogicSource,
} from '../../utilities/helpers';
import { useSurveyContext } from '../../utilities/context';
import SurveyToolbar from '../SurveyToolbar';

import AddQuestionIconButton from './components/AddQuestionIconButton';
import ModalAIGeneratedQuestionnaire from './components/ModalAIGeneratedQuestionnaire';
import ModalInstantSurveyQuestion from './components/ModalInstantSurveyQuestion';
import ModalQuestionLibrary from './components/ModalQuestionLibrary';
import ModalAttentionCheck from './components/ModalAttentionCheck';
import ModalScreenerQuestion from './components/ModalScreenerQuestion';
import ModalSurveyTemplate from './components/ModalSurveyTemplate';
import QuestionnaireList from './components/QuestionnaireList';
import {
  cleanupLogicAfterEdit,
  cleanupLogicAfterRemoval,
  resyncCanonicalPipingReferences,
  stripQuestionIds,
  syncCarryForwardAnswers,
} from './utilities';

import styles from './_index.module.scss';

interface PipingQuestionReference {
  questionId: number;
  questionText: string;
}

interface PipingQuestionsMap {
  childrenByParent: Record<number, PipingQuestionReference[]>;
  parentsByChild: Record<number, PipingQuestionReference[]>;
}

interface QuestionnaireProps {
  onNext: () => void;
  onPrevious: () => void;
}

const Questionnaire = ({ onNext, onPrevious }: QuestionnaireProps) => {
  const {
    activeGroups,
    activeQuestions,
    currentQuestionCount,
    currentQuestionnairePrice,
    instantSurveyType,
    isReadOnly,
    isResearchObjectivesTooLong,
    maxQuestions,
    persistSurveyDraft,
    saveInstantSurvey,
    setShowAIGenerationModal,
    setShowSurveyTemplateModal,
    setSurveyGoals,
    showAIGenerationModal,
    showSurveyTemplateModal,
    survey,
    updateSurvey,
  } = useSurveyContext();

  const [generationError, setGenerationError] = useState(false);
  const [generationErrorMessage, setGenerationErrorMessage] = useState('');
  const [isDetailedQuestionView, setIsDetailedQuestionView] = useState(true);
  const [isDuplicatingQuestion, setIsDuplicatingQuestion] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [previewQuestions, setPreviewQuestions] = useState([]);

  const [selectedQuestion, setSelectedQuestion] = useState<null | Question>(null);
  const [showCustomQuestionModal, setShowCustomQuestionModal] = useState(false);
  const [showQuestionsLibraryModal, setShowQuestionsLibraryModal] = useState(false);
  const [showAttentionCheckModal, setShowAttentionCheckModal] = useState(false);
  const [showScreenerQuestionModal, setShowScreenerQuestionModal] = useState(false);
  const remainingQuestionSlots = Math.max(0, maxQuestions - currentQuestionCount);
  const hasQuestionSlots = remainingQuestionSlots > 0;
  const MAX_NUM_QUESTIONS_MESSAGE =
    'You already have the maximum number of questions. Remove one to add more.';

  const clearGenerationError = () => {
    setGenerationError(false);
    setGenerationErrorMessage('');
  };

  const { permissions } = useAppSelector((state) => state.user.data) ?? {};
  const { canUseAIGenerationQuestionnaire } = permissions ?? {};
  const { client } = useStatsigClient();
  const isMultiLevelDisqualifyEnabled = client.checkGate(GATES.MULTI_LEVEL_DISQUALIFY);

  const deletedQuestions = survey.questions.filter(({ _delete }: Question) => _delete);
  const hasTurfQuestion = activeQuestions?.some(({ turf }) => Boolean(turf));
  const hasVanWestendorpQuestion = activeQuestions?.some(
    ({ type }) => type === QUESTION_TYPES.VAN_WESTENDORP.value
  );
  const isTurfSurvey = instantSurveyType?.id === SURVEY_TYPES.TURF;
  const isVanWestendorpSurvey = instantSurveyType?.id === SURVEY_TYPES.VAN_WESTENDORP;
  const isVideoSurvey = instantSurveyType?.id === SURVEY_TYPES.VIDEO;
  const shouldShowActions = !isReadOnly && previewQuestions?.length === 0;
  const sequentialMonadicQuestionnaireError = activeQuestions
    .map(getSequentialMonadicQuestionnaireValidationError)
    .find(Boolean);
  const questionLimitExceededError =
    currentQuestionCount > maxQuestions
      ? `This survey uses ${currentQuestionCount} questions; the maximum allowed is ${maxQuestions}. Remove questions before continuing.`
      : null;
  const questionLimitReachedMessage =
    currentQuestionCount === maxQuestions
      ? `You have reached the maximum of ${maxQuestions} questions. Remove one before adding another question.`
      : null;
  const questionnaireValidationError =
    sequentialMonadicQuestionnaireError || questionLimitExceededError;
  const questionnaireAnnouncementMessage =
    questionnaireValidationError || questionLimitReachedMessage;

  const firstErrorRef = useRef<HTMLDivElement | null>(null);

  const pipingQuestionsMap = useMemo<PipingQuestionsMap>(() => {
    const map: PipingQuestionsMap = {
      childrenByParent: {},
      parentsByChild: {},
    };
    const questionsWithNormalizedPiping = resyncCanonicalPipingReferences({
      originalQuestions: activeQuestions,
      updatedQuestions: activeQuestions,
    });

    // eslint-disable-next-line security/detect-unsafe-regex
    const pipingPattern = /data-mention-id="answer from q(\d+)(?:\.\d+)?"/g;

    questionsWithNormalizedPiping.forEach((question) => {
      const currentQuestionId = question.id;
      // Deduplicate: a parent referenced in both question text and an answer body should appear once.
      const seenParentIds = new Set<number | string>();

      const textsToScan: string[] = [
        question.richText || question.text || '',
        ...((question.answers ?? []) as Array<{ richText?: string; text?: string }>).map(
          (a) => a.richText || a.text || ''
        ),
      ];

      textsToScan.forEach((textToScan) => {
        pipingPattern.lastIndex = 0;
        let match;

        while ((match = pipingPattern.exec(textToScan)) !== null) {
          const parentQuestionNumber = parseInt(match[1], 10);
          const parentQuestion = questionsWithNormalizedPiping[parentQuestionNumber - 1];
          if (!parentQuestion) continue;

          const parentQuestionIdValue = parentQuestion.id;
          if (parentQuestionIdValue === undefined || parentQuestionIdValue === null) continue;
          if (seenParentIds.has(parentQuestionIdValue)) continue;
          seenParentIds.add(parentQuestionIdValue);

          if (!map.childrenByParent[parentQuestionIdValue]) {
            map.childrenByParent[parentQuestionIdValue] = [];
          }
          map.childrenByParent[parentQuestionIdValue].push({
            questionId: currentQuestionId,
            questionText: question.richText || question.text || '',
          });

          if (!map.parentsByChild[currentQuestionId]) {
            map.parentsByChild[currentQuestionId] = [];
          }
          map.parentsByChild[currentQuestionId].push({
            questionId: parentQuestionIdValue,
            questionText: parentQuestion.richText || parentQuestion.text || '',
          });
        }
      });
    });
    return map;
  }, [survey?.questions, activeQuestions]);

  useEffect(() => {
    if (!survey?.questions?.length) return;

    const hasConceptImage =
      Array.isArray(survey?.groups) &&
      survey.groups.some((group: Group) => group?.conceptImage != null);

    if (!hasConceptImage && survey.questions.length > 0) {
      const hasQuestionsWithConceptImage = survey.questions.some(
        (question: Question) => question.showConceptImage === true && !question._delete
      );

      if (hasQuestionsWithConceptImage) {
        const updatedQuestions = survey.questions.map((question: Question) => {
          if (question._delete) return question;
          return {
            ...question,
            showConceptImage: false,
          };
        });

        updateSurvey({ questions: updatedQuestions }, { markDirty: false });
      }
    }
  }, [survey?.groups, survey?.questions]);

  useEffect(() => {
    return () => {
      setSurveyGoals('');
    };
  }, [setSurveyGoals]);

  const canPreviewSurveys = useCanPreviewSurveys();
  const {
    handleClosePreviewModal,
    handlePreviewButtonClick,
    handleUpdatePreviewQuestion,
    handleUpdatePreviewQuotaGroup,
    previewQuestions: surveyPreviewQuestions,
    previewSelection,
    resetPreviewQuestion,
    showPreviewModal,
  } = useSurveyPreview({
    questionLogic: survey.questionLogic,
    questions: activeQuestions,
    quotaGroups: activeGroups,
  });

  const cloneQuestion = async (question: Question): Promise<Question | undefined> => {
    try {
      return await createQuestion({
        question: {
          ...stripQuestionIds(question),
          folder: FOLDER_ID_CUSTOM,
        },
      });
    } catch (error) {
      console.error('Error cloning question', error);
    }
  };

  const createAIQuestion = async (question: Question) => {
    const { aiGenerated, answers, text, type } = question;

    const controller = new AbortController();
    try {
      let data;
      data = await createQuestion({
        question: {
          aiGenerated,
          answers,
          folder: FOLDER_ID_CUSTOM,
          text,
          type,
        },
        signal: controller.signal,
      });
      return data;
    } catch (error) {
      if (!controller.signal.aborted) {
        console.error(error);
        toast.error('Error creating question');
      }
    }
  };

  const handleAcceptPreview = async () => {
    const availableSlots = Math.max(0, maxQuestions - currentQuestionCount);
    if (availableSlots === 0) {
      toast(MAX_NUM_QUESTIONS_MESSAGE);
      setPreviewQuestions([]);
      return;
    }

    const questionsToCreate = previewQuestions.slice(0, availableSlots);
    if (previewQuestions.length > availableSlots) {
      toast(`Only ${availableSlots} question${availableSlots === 1 ? '' : 's'} can be added.`);
    }

    const createdQuestions = (
      await Promise.all(questionsToCreate.map((question) => createAIQuestion(question)))
    ).filter(Boolean) as Question[];

    if (!createdQuestions.length) {
      toast.error('Unable to add questions. Please try again.');
      return;
    }

    const updatedQuestions = [...activeQuestions, ...createdQuestions, ...deletedQuestions];

    updateSurvey(
      {
        questions: updatedQuestions,
      },
      { markDirty: true }
    );
    clearGenerationError();
    setPreviewQuestions([]);
    setSurveyGoals('');
  };

  const handleAddAIQuestions = (questions = []) => {
    const availableSlots = Math.max(0, maxQuestions - currentQuestionCount);

    if (availableSlots === 0) {
      toast(MAX_NUM_QUESTIONS_MESSAGE);
      setPreviewQuestions([]);
      return;
    }

    if (!questions.length) {
      setPreviewQuestions([]);
      return;
    }

    if (questions.length > availableSlots) {
      toast(`Only ${availableSlots} question${availableSlots === 1 ? '' : 's'} can be generated.`);
    }

    setPreviewQuestions(questions.slice(0, availableSlots));
  };

  const handleCancelPreview = () => {
    clearGenerationError();
    setPreviewQuestions([]);
    setSurveyGoals('');
  };

  const handleAddLibraryQuestions = async (questions: Question[]) => {
    const resolvedQuestions = (
      await Promise.all(
        questions.map((question) =>
          question.folder !== FOLDER_ID_CUSTOM ? cloneQuestion(question) : question
        )
      )
    ).filter(Boolean) as Question[];

    if (resolvedQuestions.length !== questions.length) {
      if (!resolvedQuestions.length) {
        toast.error('Unable to add library questions. Please try again.');
        return;
      }

      toast('Warning: Some library questions failed to be added.');
    }

    updateSurvey(
      {
        questions: [
          ...activeQuestions,
          ...resolvedQuestions,
          ...deletedQuestions.filter(
            // extra .filter checks for questions being added that were previously removed
            ({ id: existingQuestionId }: Question) =>
              !resolvedQuestions.some(
                ({ id: selectedQuestionId }: Question) => existingQuestionId === selectedQuestionId
              )
          ),
        ],
      },
      { markDirty: true }
    );
    clearGenerationError();
    handleQuestionLibraryModalClose();
  };

  const handleAddAttentionCheckQuestions = async (questions: Question[]) => {
    const availableSlots = Math.max(0, maxQuestions - currentQuestionCount);
    if (availableSlots === 0) {
      toast(MAX_NUM_QUESTIONS_MESSAGE);
      return;
    }

    const resolvedQuestions = questions.slice(0, availableSlots);
    if (questions.length > availableSlots) {
      toast(`Only ${availableSlots} question${availableSlots === 1 ? '' : 's'} can be added.`);
    }

    if (!resolvedQuestions.length) {
      toast.error('Unable to add attention check questions. Please try again.');
      return;
    }

    updateSurvey(
      {
        questions: [
          ...activeQuestions,
          ...resolvedQuestions,
          ...deletedQuestions.filter(
            ({ id: existingQuestionId }) =>
              !resolvedQuestions.some(
                ({ id: selectedQuestionId }) => existingQuestionId === selectedQuestionId
              )
          ),
        ],
      },
      { markDirty: true }
    );
    clearGenerationError();
    handleAttentionCheckModalClose();
  };

  const handleAddSurveyTemplate = async (template: Template) => {
    if (!Array.isArray(template?.questions) || template.questions.length === 0) {
      toast.error('Unable to apply template. Please try again.');
      return;
    }

    const clonedTemplateQuestions = (
      await Promise.all(template.questions.map((question: Question) => cloneQuestion(question)))
    ).filter(Boolean) as Question[];

    if (clonedTemplateQuestions.length !== template.questions.length) {
      if (!clonedTemplateQuestions.length) {
        toast.error('Unable to apply template. Please try again.');
        return;
      }
      toast('Warning: Some template questions failed to be added.');
    }

    const updatedQuestions = [
      ...clonedTemplateQuestions,
      ...survey.questions.map((question: Question) => ({ ...question, _delete: true })),
    ];
    updateSurvey(
      {
        carryForwardLogic: {},
        questionLogic: {},
        questions: updatedQuestions,
        surveyTemplateId: template.id,
      },
      { markDirty: true }
    );
    setPreviewQuestions([]);
    clearGenerationError();
    handleQuestionSurveyTemplateClose();
  };

  const handleAddMonadicSection = async () => {
    try {
      const data = await createQuestion({
        question: {
          answers: [],
          concepts: [],
          instantSurveyTypes: [SURVEY_TYPES.SEQUENTIAL_MONADIC],
          properties: {},
          subQuestions: [],
          text: 'Monadic Section',
          type: QUESTION_TYPES.SEQUENTIAL_MONADIC.value,
        },
      });

      handleQuestionCreate(data);
    } catch (error) {
      console.error('Error creating monadic section question block', error);
      toast.error('Error creating monadic section');
    }
  };

  const handleAddScreenerQuestion = () => {
    setShowScreenerQuestionModal(true);
  };

  const handleScreenerQuestionCreate = async (question: Question) => {
    try {
      const predefinedDisqualify = isMultiLevelDisqualifyEnabled
        ? question.properties.disqualify
        : undefined;
      if (predefinedDisqualify) {
        if (predefinedDisqualify.logic.length === 0) {
          throw new Error('Screener disqualify logic must contain at least one row');
        }
        if (!['ALL', 'ANY'].includes(predefinedDisqualify.operator)) {
          throw new Error('Screener disqualify logic has unsupported operator');
        }
        if (predefinedDisqualify.logic.some((row) => !['ANY', 'NOT'].includes(row.operator))) {
          throw new Error('Screener disqualify logic row has unsupported operator');
        }
        if (
          predefinedDisqualify.logic.some(
            (row) => !Array.isArray(row.answers) || row.answers.length === 0
          )
        ) {
          throw new Error('Screener disqualify logic row must contain answers');
        }
        const sourceAnswerIds = new Set(question.answers.map(({ id }) => id));
        const hasUnknownAnswer = predefinedDisqualify.logic.some((row) =>
          row.answers.some((answerId) => !sourceAnswerIds.has(answerId))
        );
        if (hasUnknownAnswer)
          throw new Error('Screener disqualify logic references unknown answer');
      }
      const createdQuestionProperties = { ...question.properties };
      delete createdQuestionProperties.disqualify;

      const createdQuestion = await createQuestion({
        question: {
          ...stripQuestionIds(question),
          folder: FOLDER_ID_CUSTOM,
          isScreener: true,
          properties: createdQuestionProperties,
        },
      });
      const createdScreenerQuestion = {
        ...createdQuestion,
        screenerValueMap: question.screenerValueMap ?? createdQuestion.screenerValueMap,
      };
      if (predefinedDisqualify && question.answers.length !== createdQuestion.answers.length) {
        throw new Error('Created screener answer count does not match source');
      }
      const disqualifyingAnswerTexts = new Set(
        question.answers
          .filter((answer) => answer.properties?.disqualify)
          .map((answer) => answer.text)
      );
      const disqualifyingAnswers = createdQuestion.answers.filter((answer: QuestionAnswer) =>
        disqualifyingAnswerTexts.has(answer.text)
      );
      const createdAnswerBySourceId = new Map(
        question.answers.map((answer, index) => [answer.id, createdQuestion.answers[index]])
      );
      const disqualify = predefinedDisqualify
        ? {
            logic: predefinedDisqualify.logic.map((row) => ({
              answers: row.answers.map((answerId) => createdAnswerBySourceId.get(answerId)),
              operator: QUESTION_LOGIC_OPERATORS[row.operator],
            })),
            operator: QUESTION_LOGIC_OPERATORS[predefinedDisqualify.operator],
          }
        : disqualifyingAnswers.length
          ? isMultiLevelDisqualifyEnabled
            ? {
                logic: [
                  {
                    answers: disqualifyingAnswers,
                    operator: QUESTION_LOGIC_OPERATORS.ANY,
                  },
                ],
                operator: QUESTION_LOGIC_OPERATORS.ANY,
              }
            : {
                answers: disqualifyingAnswers,
                operator: QUESTION_LOGIC_OPERATORS.ANY,
              }
          : undefined;
      const questionLogic = disqualify
        ? {
            ...survey.questionLogic,
            [createdScreenerQuestion.id]: {
              disqualify,
            },
          }
        : survey.questionLogic;

      const questionsWithScreener = getQuestionsWithScreenersFirst([
        ...activeQuestions,
        createdScreenerQuestion,
        ...deletedQuestions,
      ]);
      const questionsWithResyncedPiping = resyncCanonicalPipingReferences({
        originalQuestions: activeQuestions,
        updatedQuestions: questionsWithScreener,
      });

      updateSurvey(
        {
          questionLogic,
          questions: questionsWithResyncedPiping,
        },
        { markDirty: true }
      );
      clearGenerationError();
      handleScreenerQuestionModalClose();
    } catch (error) {
      console.error('Error creating screener question', error);
      toast.error('Error creating screener question');
    }
  };

  const handleQuestionCreate = async (
    question: Question,
    carryForwardLogic?: CarryForwardLogic
  ) => {
    updateSurvey(
      {
        ...(carryForwardLogic
          ? {
              carryForwardLogic: {
                ...(survey.carryForwardLogic || {}),
                [question.id]: carryForwardLogic,
              },
            }
          : {}),
        questions: [...activeQuestions, question, ...deletedQuestions],
      },
      { markDirty: true }
    );
    clearGenerationError();
    handleQuestionModalClose();

    try {
      // ensure state has updated before saving draft
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      await persistSurveyDraft({ reason: 'create-question' });
    } catch (error) {
      console.error('Error saving draft after question creation', error);
      toast.error(ERROR_SAVE_DRAFT);
    }
  };

  const handleQuestionDuplicate = (question: Question) => {
    setIsDuplicatingQuestion(true);
    setSelectedQuestion(question);
    setShowCustomQuestionModal(true);
  };

  const handleQuestionEdit = (question: Question) => {
    setSelectedQuestion(question);
    if (question.isScreener) {
      setShowScreenerQuestionModal(true);
      return;
    }

    setShowCustomQuestionModal(true);
  };

  const handleQuestionLibraryModalClose = () => {
    setShowQuestionsLibraryModal(false);
    setSelectedQuestion(null);
  };

  const handleQuestionSurveyTemplateClose = () => {
    setShowSurveyTemplateModal(false);
  };

  const handleAttentionCheckModalClose = () => {
    setShowAttentionCheckModal(false);
  };

  const handleScreenerQuestionModalClose = () => {
    setShowScreenerQuestionModal(false);
    setSelectedQuestion(null);
  };

  const handleQuestionModalClose = () => {
    setShowCustomQuestionModal(false);

    setIsDuplicatingQuestion(false);
    setSelectedQuestion(null);
  };

  const handleQuestionRemove = async (question: Question) => {
    const updatedQuestions = [...survey.questions];
    const questionIndex = updatedQuestions.findIndex(({ id }) => id === question.id);

    if (questionIndex < 0) {
      toast.error('Unable to remove question. Please refresh and try again.');
      return;
    }

    const [removedQuestion] = updatedQuestions.splice(questionIndex, 1);
    const nextRemovedQuestion = {
      ...removedQuestion,
      answers: removedQuestion.answers.map((answer: QuestionAnswer) =>
        answer.isCarryForwardGenerated ? { ...answer, _delete: true } : answer
      ),
    };

    const hasCarryForwardGeneratedAnswers = nextRemovedQuestion.answers.some(
      ({ isCarryForwardGenerated }: QuestionAnswer) => isCarryForwardGenerated
    );

    if (hasCarryForwardGeneratedAnswers) {
      try {
        await updateQuestion({
          question: nextRemovedQuestion,
        });
      } catch (error) {
        console.error('Error removing carry-forward generated answers', error);
        toast.error('Error updating question answers');
        return;
      }
    }

    const newCarryForwardLogic = { ...(survey.carryForwardLogic || {}) };
    delete newCarryForwardLogic[question.id];
    nextRemovedQuestion._delete = true;
    // A deleted question belongs to no group; clearing this prevents it from referencing a group
    // that gets removed in the same tick (e.g. removing a group's last member).
    nextRemovedQuestion.questionGroup = null;
    const questionsAfterRemoval = [...updatedQuestions, nextRemovedQuestion];
    const questionsWithResyncedPiping = removedQuestion.isScreener
      ? resyncCanonicalPipingReferences({
          originalQuestions: activeQuestions,
          updatedQuestions: questionsAfterRemoval,
        })
      : questionsAfterRemoval;

    updateSurvey(
      {
        carryForwardLogic: newCarryForwardLogic,
        questionLogic: cleanupLogicAfterRemoval({
          question: nextRemovedQuestion,
          questionId: question.id,
          questionLogic: survey.questionLogic,
          questions: updatedQuestions,
        }),
        questions: questionsWithResyncedPiping,
        // INSRV-2109: remove surveyTemplateId if all questions are removed from the questionnaire
        surveyTemplateId:
          updatedQuestions.filter(({ _delete }) => !_delete).length > 0
            ? survey.surveyTemplateId
            : null,
      },
      { markDirty: true }
    );

    try {
      // ensure state has updated before saving draft
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      await persistSurveyDraft({ reason: 'remove-question' });
    } catch (error) {
      console.error('Error saving draft after question removal', error);
      toast.error(ERROR_SAVE_DRAFT);
    }
  };

  const handleQuestionUpdate = async (
    question: Question,
    carryForwardLogic?: CarryForwardLogic,
    closeModal = handleQuestionModalClose
  ) => {
    const questionIndex = survey.questions.findIndex(
      ({ id }: Question) => id === selectedQuestion?.id
    );
    const updatedQuestions = [...survey.questions];

    // questionGroup is write-only on the backend (it returns question_group_path instead), so the
    // updated question coming back from the modal/API has no questionGroup. Carry forward the
    // client-managed group membership so editing a grouped question doesn't drop it from its group.
    const mergedQuestion = { ...question, questionGroup: selectedQuestion?.questionGroup };

    const [removedQuestion] = updatedQuestions.splice(questionIndex, 1, mergedQuestion);
    if (removedQuestion.id !== question.id) {
      // a recommended question became a custom question, remove the original question
      removedQuestion._delete = true;
      updatedQuestions.push(removedQuestion);
    }

    const newCarryForwardLogic = { ...(survey.carryForwardLogic || {}) };
    if (carryForwardLogic) {
      newCarryForwardLogic[question.id] = carryForwardLogic;
    } else {
      delete newCarryForwardLogic[question.id];
    }

    // Propagate the edited source options onto every question that carries forward from it.
    let syncedQuestions = updatedQuestions;

    if (Object.keys(newCarryForwardLogic).length > 0) {
      try {
        // The latest version of each question we've changed so far, keyed by id.
        // Seeded with the just-edited question so its direct destinations pick
        // up the edit; each synced destination is added so the next link sees it.
        const updatedById: Record<string, Question> = { [question.id]: mergedQuestion };

        syncedQuestions = [];
        for (const currentQuestion of updatedQuestions) {
          const sourceId = newCarryForwardLogic[currentQuestion.id]?.sourceSurveyQuestionId;
          const sourceQuestion = sourceId != null ? updatedById[sourceId] : undefined;

          // Only re-sync when this question carries forward from a question
          if (!sourceQuestion) {
            syncedQuestions.push(currentQuestion);
            continue;
          }

          const syncedWithAnswers = syncCarryForwardAnswers({
            destinationQuestion: currentQuestion,
            sourceQuestion,
          });

          const nextQuestion =
            typeof syncedWithAnswers.id === 'number'
              ? await updateQuestion({ question: syncedWithAnswers })
              : syncedWithAnswers;

          updatedById[currentQuestion.id] = nextQuestion;
          syncedQuestions.push(nextQuestion);
        }
      } catch (error) {
        console.error('Error propagating carry-forward answers', error);
        toast.error('Error updating carry forward answers');
        return;
      }
    }

    if (questionIndex >= 0) {
      updateSurvey(
        {
          carryForwardLogic: newCarryForwardLogic,
          questionLogic: cleanupLogicAfterEdit({
            originalQuestion: selectedQuestion,
            question,
            questionLogic: survey.questionLogic,
            questions: syncedQuestions,
          }),
          questions: syncedQuestions,
        },
        { markDirty: true }
      );
    }
    closeModal();

    try {
      // ensure state has updated before saving draft
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      await persistSurveyDraft({ reason: 'update-question' });
    } catch (error) {
      console.error('Error saving draft after question update', error);
      toast.error(ERROR_SAVE_DRAFT);
    }
  };

  const handleScreenerQuestionUpdate = async (question: Question) => {
    const updatedQuestion = await updateQuestion({ question });

    await handleQuestionUpdate(
      {
        ...updatedQuestion,
        isScreener: true,
        screenerLabel: question.screenerLabel ?? updatedQuestion.screenerLabel,
        screenerValueMap: question.screenerValueMap ?? updatedQuestion.screenerValueMap,
      },
      undefined,
      handleScreenerQuestionModalClose
    );
  };

  const canGenerateMoreQuestions = () => remainingQuestionSlots > 0;

  const openAIGenerationModal = ({ resetGoals = false } = {}) => {
    if (!canGenerateMoreQuestions()) {
      toast(MAX_NUM_QUESTIONS_MESSAGE);
      return;
    }
    if (resetGoals) {
      setSurveyGoals('');
    }
    setShowAIGenerationModal(true);
  };

  const onContinue = async () => {
    if (isReadOnly) {
      onNext();
      return;
    }
    if (isTurfSurvey && !hasTurfQuestion) {
      toast.error('Please add at least one turf question before continuing.');
      return;
    }
    if (isVanWestendorpSurvey && !hasVanWestendorpQuestion) {
      toast.error('Please add at least one Van Westendorp question before continuing.');
      return;
    }
    if (questionnaireValidationError) {
      toast.error(questionnaireValidationError);
      return;
    }
    const isLogicValid = validateQuestionLogic();
    if (!isLogicValid) {
      if (firstErrorRef.current) {
        firstErrorRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      }
      return;
    }

    setIsSaving(true);
    try {
      await saveInstantSurvey();
    } catch (error) {
      console.error(error);
      toast.error(ERROR_SAVE_DRAFT);
      setIsSaving(false);
      return;
    }

    onNext();
  };

  const validateQuestionLogic = () => {
    const modifiedQuestionLogic = { ...survey.questionLogic };
    let isValid = true;

    const isInvalidQuestionSource = ({
      logicQuestion,
      sourceQuestion,
    }: {
      logicQuestion?: Question;
      sourceQuestion?: null | Question;
    }) =>
      !sourceQuestion?.id ||
      !logicQuestion ||
      !isValidQuestionLogicSource({
        currentQuestion: logicQuestion,
        questions: activeQuestions,
        sourceQuestion,
      });

    for (const questionId in modifiedQuestionLogic) {
      const question = modifiedQuestionLogic[questionId];
      const logicQuestion = findQuestionLogicQuestionById(activeQuestions, questionId) as Question;

      if (question.skip) {
        for (const row of question.skip.logic) {
          if ('url_variable' in row) {
            const quotaGroupIds = survey.groups.map(({ id }: Group) => id);
            if (!row.values || row.values.length === 0) {
              row.hasAnswerError = true;
              isValid = false;
            } else {
              let quotaGroupsValid = true;
              for (const groupID of row.values) {
                if (!quotaGroupIds.includes(Number(groupID))) {
                  quotaGroupsValid = false;
                }
              }
              if (!quotaGroupsValid) {
                row.hasAnswerError = true;
                isValid = false;
              } else {
                row.hasAnswerError = false;
              }
            }
          } else if ('comparison_value' in row) {
            if (isInvalidQuestionSource({ logicQuestion, sourceQuestion: row.question })) {
              row.hasQuestionError = true;
              row.isFirstQuestionError = isValid;
              isValid = false;
            } else {
              row.hasQuestionError = false;
            }

            if (
              // check for empty comparison_value
              row.comparison_value === undefined ||
              row.comparison_value === null ||
              // @ts-ignore - numeric input can possibly return an empty string, keep as sanity check
              row.comparison_value === '' ||
              // check for invalid numeric value
              (row.question?.type === QUESTION_TYPES.NUMERIC.value &&
                !isBetweenMinAndMax({
                  max: row.question.properties?.maxNumber,
                  min: row.question.properties?.minNumber,
                  value: row.comparison_value,
                }))
            ) {
              row.hasAnswerError = true;
              row.isFirstAnswerError = isValid;
              isValid = false;
            } else {
              row.hasAnswerError = false;
            }
          } else if ('answers' in row) {
            if (isInvalidQuestionSource({ logicQuestion, sourceQuestion: row.question })) {
              row.hasQuestionError = true;
              row.isFirstQuestionError = isValid;
              isValid = false;
            } else {
              row.hasQuestionError = false;
            }
            if ((row.answers || []).length === 0) {
              row.hasAnswerError = true;
              row.isFirstAnswerError = isValid;
              isValid = false;
            } else {
              row.hasAnswerError = false;
            }
          }
        }
      }

      if (question.disqualify) {
        const disqualifyLogic = question.disqualify;
        if ('logic' in disqualifyLogic && Array.isArray(disqualifyLogic.logic)) {
          for (const row of disqualifyLogic.logic) {
            if ('url_variable' in row) {
              if ((row.values || []).length === 0) {
                row.hasAnswerError = true;
                isValid = false;
              } else {
                row.hasAnswerError = false;
              }
            } else if ('answers' in row && (row.answers || []).length === 0) {
              row.isFirstAnswerError = isValid;
              row.hasAnswerError = true;
              isValid = false;
            } else {
              row.hasAnswerError = false;
            }
          }
        } else if ('answers' in disqualifyLogic && (disqualifyLogic.answers || []).length === 0) {
          disqualifyLogic.isFirstAnswerError = isValid;
          disqualifyLogic.hasAnswerError = true;
          isValid = false;
        } else if ('hasAnswerError' in disqualifyLogic) {
          disqualifyLogic.hasAnswerError = false;
        }
      }
    }

    updateSurvey({ questionLogic: modifiedQuestionLogic }, { markDirty: true });
    return isValid;
  };

  const isMinimumQuestionsThresholdUnmet = currentQuestionCount < minQuestions;
  const hasQuestionsWithMissingAnswersOrRows = activeQuestions.some((question) => {
    if (QUESTION_TYPES_WITH_OPTIONS.includes(question.type) && question.answers.length === 0)
      return true;
    if (question.type === QUESTION_TYPES['TABLE-RADIO'].value && question.answers.length === 0)
      // column check
      return true;
    if (
      QUESTION_TYPES_WITH_SUBQUESTIONS.includes(question.type) &&
      question.subQuestions.length === 0
    )
      return true;

    return false;
  });

  const isContinueDisabled =
    isMinimumQuestionsThresholdUnmet ||
    isResearchObjectivesTooLong ||
    Boolean(questionnaireValidationError) ||
    hasQuestionsWithMissingAnswersOrRows;

  return (
    <>
      <ActionArea
        buttons={[
          previewQuestions.length > 0
            ? [
                <Button
                  data-testid="cancel-button"
                  key="cancel-button"
                  onClick={handleCancelPreview}
                  text="Cancel"
                  variant="secondary"
                />,
                <Button
                  data-testid="insert-button"
                  key="insert-button"
                  onClick={handleAcceptPreview}
                  text="Insert Questions"
                  variant="ai"
                />,
              ]
            : [
                <Button
                  data-testid="previous-button"
                  key="previous-button"
                  onClick={onPrevious}
                  text="Previous"
                  variant="secondary"
                />,
                <Button
                  data-testid="review-button"
                  isDisabled={isContinueDisabled}
                  isLoading={isSaving}
                  key="review-button"
                  onClick={onContinue}
                  text="Go To Review"
                />,
              ],
        ]}
        className={styles['questionnaire']}
      >
        <SurveyToolbar isSaveAsDraftDisabled={isResearchObjectivesTooLong} />
        <div className={styles['questionnaire-pricing']}>
          <Toolbar
            tools={[
              ...(shouldShowActions
                ? [
                    <AddQuestionIconButton
                      key="add-question"
                      onAddAttentionCheck={() => setShowAttentionCheckModal(true)}
                      onAddMonadicSection={() => handleAddMonadicSection()}
                      onAddQuestion={() => setShowQuestionsLibraryModal(true)}
                      onCreateQuestion={() => setShowCustomQuestionModal(true)}
                      onCreateScreenerQuestion={handleAddScreenerQuestion}
                    />,
                    ...(!isVideoSurvey && canUseAIGenerationQuestionnaire
                      ? [
                          <div key="ai-generation">
                            <IconButton
                              isDisabled={!hasQuestionSlots}
                              onClick={() => {
                                if (!hasQuestionSlots) return;
                                openAIGenerationModal({ resetGoals: true });
                              }}
                              tooltip={
                                hasQuestionSlots ? 'AI Generated Questionnaire' : maxQuestionError
                              }
                            >
                              <GPTOutlinedIcon />
                            </IconButton>
                          </div>,
                        ]
                      : []),
                    ...(!isVideoSurvey
                      ? [
                          <IconButton
                            data-log="SurveyTemplates_Open"
                            data-log-properties={JSON.stringify({ type: instantSurveyType.id })}
                            key="survey-templates"
                            onClick={() => setShowSurveyTemplateModal(true)}
                            tooltip="Survey Templates"
                          >
                            <OverviewIcon />
                          </IconButton>,
                        ]
                      : []),
                    <OverlayTriggerTooltip
                      content={'Change to ' + (isDetailedQuestionView ? 'Overview' : 'Detailed')}
                      key="change-view"
                    >
                      <button
                        data-testid="change-view-button"
                        onClick={() => setIsDetailedQuestionView(!isDetailedQuestionView)}
                      >
                        {isDetailedQuestionView ? <ContractIcon /> : <ExpandIcon />}
                      </button>
                    </OverlayTriggerTooltip>,
                    <DownloadDOCXButton key="download-docx" survey={survey} />,
                    ...(canPreviewSurveys
                      ? [
                          <PreviewButton
                            isDisabled={getIsPreviewDisabled(surveyPreviewQuestions)}
                            key="preview-button"
                            location="questionnaire tab in verified voices"
                            onClick={() => handlePreviewButtonClick()}
                          />,
                        ]
                      : []),
                  ]
                : []),
            ]}
          >
            {!isVideoSurvey && <p>{currentQuestionnairePrice?.description}</p>}
          </Toolbar>
        </div>
        {questionnaireAnnouncementMessage && (
          <Announcement text={questionnaireAnnouncementMessage} variant="error" />
        )}
        <QuestionnaireList
          calculateQuestionNumbers={calculateQuestionNumbers}
          canPreviewSurvey={canPreviewSurveys}
          firstErrorRef={firstErrorRef}
          generationError={generationError}
          generationErrorMessage={generationErrorMessage}
          isDetailedQuestionView={isDetailedQuestionView}
          isVideoSurvey={isVideoSurvey}
          onAcceptPreview={handleAcceptPreview}
          onCancelPreview={handleCancelPreview}
          onCreateQuestion={() => setShowCustomQuestionModal(true)}
          onDuplicateQuestion={handleQuestionDuplicate}
          onEditQuestion={handleQuestionEdit}
          onOpenAIGenerator={() => openAIGenerationModal({ resetGoals: true })}
          onPreviewSurvey={handlePreviewButtonClick}
          onRemoveQuestion={handleQuestionRemove}
          onReprompt={() => {
            if (!canGenerateMoreQuestions()) {
              toast(MAX_NUM_QUESTIONS_MESSAGE);
              return;
            }
            clearGenerationError();
            openAIGenerationModal();
          }}
          pipingQuestionsMap={pipingQuestionsMap}
          questions={previewQuestions.length > 0 ? previewQuestions : activeQuestions}
          showActions={shouldShowActions}
          showPreviewBanner={previewQuestions.length > 0}
        />

        <div className={styles['questionnaire-list-actions']}>
          {!generationError && shouldShowActions && (
            <AddQuestionIconButton
              onAddAttentionCheck={() => setShowAttentionCheckModal(true)}
              onAddMonadicSection={() => handleAddMonadicSection()}
              onAddQuestion={() => setShowQuestionsLibraryModal(true)}
              onCreateQuestion={() => setShowCustomQuestionModal(true)}
              onCreateScreenerQuestion={handleAddScreenerQuestion}
            />
          )}
        </div>
      </ActionArea>
      {showAIGenerationModal && (
        <ModalAIGeneratedQuestionnaire
          initialSurveyLength={Math.min(Math.max(remainingQuestionSlots, 0), 5)}
          maxQuestionsAvailable={remainingQuestionSlots}
          onAddAIQuestions={handleAddAIQuestions}
          onClose={() => setShowAIGenerationModal(false)}
          onGenerationError={(val: boolean, reason: string) => {
            if (val) {
              setGenerationError(true);
              setGenerationErrorMessage(reason || '');
              setPreviewQuestions([]);
            } else {
              clearGenerationError();
            }
          }}
        />
      )}
      {showCustomQuestionModal && (
        <ModalInstantSurveyQuestion
          isDuplicate={isDuplicatingQuestion}
          onClose={handleQuestionModalClose}
          onCreate={handleQuestionCreate}
          onUpdate={handleQuestionUpdate}
          originalQuestion={selectedQuestion}
          pipingQuestionsMap={pipingQuestionsMap}
        />
      )}
      {showQuestionsLibraryModal && (
        <ModalQuestionLibrary
          maxQuestions={maxQuestions}
          onClose={handleQuestionLibraryModalClose}
          onSelectQuestions={handleAddLibraryQuestions}
          selectedQuestions={activeQuestions}
          surveyType={instantSurveyType}
        />
      )}
      {showSurveyTemplateModal && (
        <ModalSurveyTemplate
          hasWarning={activeQuestions.length > 0}
          onClose={handleQuestionSurveyTemplateClose}
          onSelectSurveyTemplate={handleAddSurveyTemplate}
          surveyType={instantSurveyType}
        />
      )}
      {showScreenerQuestionModal &&
        (selectedQuestion?.isScreener ? (
          <ModalScreenerQuestion
            initialQuestion={selectedQuestion}
            mode="edit"
            onClose={handleScreenerQuestionModalClose}
            onUpdateQuestion={handleScreenerQuestionUpdate}
          />
        ) : (
          <ModalScreenerQuestion
            mode="create"
            onClose={handleScreenerQuestionModalClose}
            onSelectQuestion={handleScreenerQuestionCreate}
          />
        ))}
      {showAttentionCheckModal && (
        <ModalAttentionCheck
          maxQuestions={maxQuestions}
          onClose={handleAttentionCheckModalClose}
          onSelectQuestions={handleAddAttentionCheckQuestions}
          selectedQuestions={activeQuestions}
        />
      )}
      {canPreviewSurveys && showPreviewModal && (
        <PreviewModal
          country={survey.country}
          instantSurveyId={survey.id!}
          isActive={showPreviewModal}
          onClose={handleClosePreviewModal}
          onQuestionChange={handleUpdatePreviewQuestion}
          onQuotaGroupChange={handleUpdatePreviewQuotaGroup}
          onRestart={resetPreviewQuestion}
          previewSelection={previewSelection}
          questions={surveyPreviewQuestions}
          quotaGroups={activeGroups}
        />
      )}
    </>
  );
};

export default Questionnaire;
